/**
 * APM Click Tracking Enhancement
 * 
 * Automatically extracts meaningful names for user interactions from buttons/elements.
 * 
 * Priority order for name extraction:
 * 1. data-apm-name attribute (explicit naming)
 * 2. aria-label attribute (recommended - improves accessibility)
 * 3. title attribute
 * 4. Button text content
 * 5. Class names (fallback)
 * 
 * Examples:
 * <button aria-label="Upload Patient Radiographs">Upload</button>
 * <button data-apm-name="Save Patient">Save</button>
 * <button title="Run AI Analysis">Analyze</button>
 * 
 * APM will use these names instead of generic "Click - button"
 */

/**
 * Get a meaningful name from an element
 */
function getElementName(element: HTMLElement): string | null {
  // 1. Check for explicit data-apm-name attribute (highest priority)
  const explicitName = element.getAttribute('data-apm-name');
  if (explicitName?.trim()) return explicitName.trim();

  // 2. Check for aria-label (good for accessibility)
  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel?.trim()) return ariaLabel.trim();

  // 3. Check for title attribute
  const title = element.getAttribute('title');
  if (title?.trim()) return title.trim();

  // 4. For buttons/links, use their text content (but filter out common icon-only text)
  if (element.tagName === 'BUTTON' || element.tagName === 'A') {
    const text = element.textContent?.trim();
    if (text && text.length > 0 && text.length < 50) {
      // Skip if it's just whitespace or single characters (likely icon buttons)
      if (text.length > 1 && !/^[\s\u00A0]+$/.test(text)) {
        return text;
      }
    }
    
    // Check for common class patterns as final fallback
    const classList = element.className;
    if (classList && typeof classList === 'string') {
      // Look for descriptive class names
      const match = classList.match(/(?:btn|button)[-_]?(\w+)/i);
      if (match && match[1]) {
        return match[1].charAt(0).toUpperCase() + match[1].slice(1);
      }
    }
  }

  // 5. Check for input buttons
  if (element.tagName === 'INPUT') {
    const input = element as HTMLInputElement;
    if (input.type === 'submit' || input.type === 'button') {
      const value = input.value?.trim();
      if (value) return value;
    }
  }

  // 6. Check for role attribute (like role="button")
  const role = element.getAttribute('role');
  if (role === 'button' || role === 'link') {
    const text = element.textContent?.trim();
    if (text && text.length > 0 && text.length < 50) {
      return text;
    }
  }

  return null;
}

// Store the last clicked element name temporarily (module-level)
let lastClickedName: string | null = null;

export const initializeApmClickTracking = () => {
  // Listen for all clicks (before APM processes them)
  document.addEventListener('click', (event) => {
    try {
      const target = event.target as HTMLElement;
      
      // Find clickable element (handles SVG children automatically via closest())
      const clickableElement = target.closest(
        'button, a, [role="button"], [role="link"], input[type="submit"], input[type="button"], [data-apm-name]'
      ) as HTMLElement;
      
      if (clickableElement) {
        const transactionName = getElementName(clickableElement);
        if (transactionName) {
          lastClickedName = transactionName;
        }
      }
    } catch (error) {
      // Silently fail - don't break user clicks if APM tracking fails
      console.error('[APM] Click tracking error:', error);
    }
  }, { capture: true });
};

/**
 * Get the last clicked element name (used by filter)
 * Clears the stored name after reading to prevent reuse
 */
export const getLastClickedName = () => {
  const name = lastClickedName;
  lastClickedName = null; // Clear immediately after reading
  return name;
};
